import { VideoContextErrorCode, VideoGlContext } from '../video-context';
import { VideoTrackSourceNode } from './source';
import { CenterFace } from './fc';
import ErrorCode from '../../common/error-code';
import RtcError from '../../common/rtc-error';
import { isRotate90Or270 } from '../../utils';
import type { mat4 } from 'gl-matrix';
import type { VisionTaskRegistry } from '../vision';
import type { ImageSegmenterResult } from '@mediapipe/tasks-vision';
import { PostProcessing } from '../video-manager';
import { isMobile } from '../../utils/environment';

type Matrix4 = typeof mat4;

interface VBNodeOpt {
  input: MediaStreamVideoTrack;
  mirror?: boolean;
  rotation?: 0 | 90 | 180 | 270;
  bg?: HTMLImageElement | 'color' | 'blur';
  blurRadius?: number;
  color: [number, number, number];
  selfModel?: boolean;
  waterMark?: {
    x: number;
    y: number;
    width: number;
    height: number;
    image: HTMLImageElement;
  };
  useTflite?: boolean;
  assetPath?: string;
  Wasm: any;
  beautyParams?: {
    beauty: number;
    brightness: number;
    ruddy: number;
  };
  width?: number;
  height?: number;
  enableFaceCentering?: boolean;
  enableEffectOptimization?: boolean;
  onAbort?: (err: any) => void;
  mat4?: Matrix4;
  postProcessing?: PostProcessing;
}
export enum VirtualBackgroundMode {
  Blur = 1,
  Image = 2,
  Color = 3,
}
const vertexShaderSource = `#version 300 es
in vec2 a_position;
in vec2 a_texCoord;
out vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_position.x, a_position.y, 0, 1);
  v_texCoord = a_texCoord;
}`;
const fragmentShaderSource = `#version 300 es
precision highp float;
uniform sampler2D u_texture;
uniform sampler2D mask;

in vec2 v_texCoord;
out vec4 outColor;
void main() {
  outColor = vec4(texture(u_texture, v_texCoord).rgb, texture(mask, v_texCoord).a);
}`;

export class VBNode extends VideoTrackSourceNode<VideoGlContext> {
  private _bgTexture?: WebGLTexture;
  private _waterMarkTexture?: WebGLTexture;
  private _lastMaskTexture?: WebGLTexture;
  private _lastMaskFbo?: WebGLFramebuffer;
  // private _lastFrameTexture?: WebGLTexture;
  private _textureValid = false;
  private _selfieTextureValid = false;
  private _selfieSegmentationHash?: string;
  wasm: any;
  predictReady: Promise<void>;
  private _color: [number, number, number];
  private _prePrograme?: WebGLProgram;
  private _segmentationMask?: ImageSegmenterResult;
  private _weixin = false;
  private _centerFace?: CenterFace;
  private _textureMatrixLocation: WebGLUniformLocation; // 实现裁剪居中方案
  private _offsetMatrixLocation: WebGLUniformLocation; // 实现人像居中方案
  private _colorLocation: WebGLUniformLocation;
  private _enableFaceCentering = false;
  private _enableEffectOptimization = false;
  private _mat4?: Matrix4; // 用以生成矩阵
  private _postProcessing?: PostProcessing;
  private _onAbort?: (err: any) => void;
  private _visionTaskRegistry?: VisionTaskRegistry;
  private resolvePreditReady: () => void;
  constructor(context: VideoGlContext, opt: VBNodeOpt) {
    super(context, opt.input, {
      name: 'vb',
      create2d: false,
      useDefaultProgram: false,
      useFbo: false,
      createTexture: true,
      width: opt.width,
      height: opt.height,
      logger: context.log
    });
    this.init(opt).catch(err => {
      context.log.error(err);
      context.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.SEGMENTATION_FAILED,
        message: `init vb node error ${(err as any).message || err}`
      }));
      this.resolvePreditReady();
    });
  }
  async init(opt: VBNodeOpt) {
    this.predictReady = new Promise(resolve => {
      this.resolvePreditReady = resolve;
    });
    const wasm = opt.Wasm;
    const gl = this.context.ctx!;
    if (opt.color) this._color = opt.color;
    if (opt.mat4) this._mat4 = opt.mat4;
    if (opt.postProcessing) this._postProcessing = opt.postProcessing;
    this._enableFaceCentering = opt.enableFaceCentering ?? false;
    this._enableEffectOptimization = opt.enableEffectOptimization ?? false;
    this.wasm = new wasm.AllIn1(gl);
    this.wasm.blurRadius = opt.blurRadius || 3;
    this.wasm.mirror = !!opt.mirror;
    this.wasm.rotation = opt.rotation || 0;
    this.wasm.vbMode = opt.bg === 'blur' ? VirtualBackgroundMode.Blur : opt.bg instanceof HTMLImageElement ? VirtualBackgroundMode.Image : opt.bg === 'color' ? VirtualBackgroundMode.Color : 0;
    this._onAbort = opt.onAbort;
    if (!opt.bg) this.resolvePreditReady();
    if (opt.waterMark) {
      const { x, y, width, height } = opt.waterMark;
      this.wasm.setWaterMark(x, y, width, height);
    }
    if (opt.beautyParams) {
      const { beauty, brightness, ruddy } = opt.beautyParams;
      this.wasm.setBeauty(beauty, brightness, ruddy, opt?.width, opt?.height);
    }
    this.program = this.wasm.init();
    this.useProgram();
    this.setAttributes(this.positionBuffer, this.texCoordBuffer);
    gl.uniform1i(gl.getUniformLocation(this.program!, 'mask')!, 1);
    if (opt.bg instanceof HTMLImageElement) {
      gl.uniform1i(gl.getUniformLocation(this.program!, 'bg')!, 2);
      this._bgTexture = this.createTexture(opt.bg);
    }
    if (opt.waterMark) {
      gl.uniform1i(gl.getUniformLocation(this.program!, 'waterMark')!, 3);
      this._waterMarkTexture = this.createTexture(opt.waterMark.image);
    }
    const identityMatrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1
    ]);
    this._textureMatrixLocation = gl.getUniformLocation(this.program!, 'u_textureMatrix')!;
    gl.uniformMatrix4fv(this._textureMatrixLocation, false, identityMatrix);
    this._offsetMatrixLocation = gl.getUniformLocation(this.program!, 'u_offsetMatrix')!;
    gl.uniformMatrix4fv(this._offsetMatrixLocation, false, identityMatrix);

    this._colorLocation = gl.getUniformLocation(this.program!, 'u_color')!;

    gl.uniform1i(gl.getUniformLocation(this.program!, 'lastMask')!, 4);
    // gl.uniform1i(gl.getUniformLocation(this.program!, 'lastResult')!, 5);
    if (this._weixin) {
      const fs = this.context.createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
      const vs = this.context.createShader(gl.VERTEX_SHADER, vertexShaderSource);
      this._prePrograme = this.context.createProgram(vs, fs);
      gl.useProgram(this._prePrograme);
      this.setAttributes(this.positionBuffer, this.texCoordBuffer);
      gl.uniform1i(gl.getUniformLocation(this._prePrograme!, 'mask')!, 1);
    }
    // todo: 后处理在IOS17存在下行拉流黑屏问题
    if (this._enableEffectOptimization && (this.wasm.vbMode === VirtualBackgroundMode.Image || this.wasm.vbMode === VirtualBackgroundMode.Color)) {
      if (isMobile()) {
        this._postProcessing = undefined;
        this.log.warn('Virtual background post-processing isn\'t allowed on mobile.');
      } else {
        this._postProcessing?.init(gl as WebGL2RenderingContext, this.positionBuffer, this.texCoordBuffer, 4 / 3);
      }
    } else this._postProcessing = undefined;
    await this.initVisionTasks(opt);
  }
  async initVisionTasks(opt: VBNodeOpt) {
    if (!opt.bg) return;
    // getInstance() without a path now awaits the in-flight initVision() promise,
    // so visionWasm is guaranteed to be ready when this resolves.
    if (!window.VisionTaskRegistry) throw new Error("Virtual background assets not found. Please redeploy the assets of the npm package.");
    this._visionTaskRegistry = await window.VisionTaskRegistry.getInstance();
    if (!this._visionTaskRegistry?.visionWasm) {
      throw new Error('Virtual background assets not found. Please redeploy the assets of the npm package.');
    }

    this._selfieSegmentationHash = await this._visionTaskRegistry.register(window.VisionTaskType.ImageSegmenter, { canvas: this.context._canvas });
    this._visionTaskRegistry.setVideo(this._selfieSegmentationHash!, this.image) ;

    if (this._enableFaceCentering) {
      try {
        if (!this._visionTaskRegistry.models.has(window.VisionTaskType.FaceDetector)) await this._visionTaskRegistry.preloadModels([window.VisionTaskType.FaceDetector]);
        const faceDetectorHash = await this._visionTaskRegistry.register(window.VisionTaskType.FaceDetector);
        if (!faceDetectorHash) return;
        this._centerFace = new CenterFace(this.wasm.vbMode, this.image, faceDetectorHash, this._visionTaskRegistry, this.context.log);
      } catch (e) {
        this.log.error('Face detector model not found. Please redeploy the assets of the npm package.');
      }
    }
  }
  onPredict(segmentationMask: ImageSegmenterResult) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    if (this._weixin) {
      if (!this._lastMaskTexture) {
        this._lastMaskTexture = this.createTexture(this.image);
        this._lastMaskFbo = this.createFramebuffer(this._lastMaskTexture);
      }
    }
    const mask = this.getMaskTexture(segmentationMask);
    if (!mask) return;
    let finalTexture = mask;
    if (this._postProcessing) {
      this._postProcessing.ratio = this.image.videoWidth / this.image.videoHeight;
      finalTexture = this._postProcessing.postProcessing(mask);
    }
    this.useProgram();
    this.setAttributes(this.positionBuffer, this.texCoordBuffer);
    this.useTexture();
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, finalTexture || null);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this._bgTexture || null);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this._waterMarkTexture || null);
    if (this.wasm.vbMode === VirtualBackgroundMode.Color) gl.uniform3fv(this._colorLocation, this._color);
    this.useBufferFrame();
    this._segmentationMask = segmentationMask;
    this.totalFrames++;
    this.centerFace();
    if (isRotate90Or270(this.wasm.rotation)) this.resize(this.image.height, this.image.width);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    segmentationMask.close();
  }
  getMaskTexture(segmentationMask: ImageSegmenterResult) {
    if (!segmentationMask.confidenceMasks) return;
    const mask = segmentationMask.confidenceMasks[0].getAsWebGLTexture();
    return mask;
  }
  onFirstFrame(): void {
    this.waitingFirstFrame = false;
    // Guard: context may have been destroyed if VB initialization failed
    if (!this.context?.ctx) return;
    const gl = this.context.ctx as WebGL2RenderingContext;
    this.useTexture();
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.image);
  }
  render(seq: number): boolean {
    const gl = this.context.ctx as WebGL2RenderingContext;
    const { image } = this;
    this.tryVideoFrameCallback();
    let { videoWidth: width, videoHeight: height } = image;
    if (isRotate90Or270(this.wasm.rotation) && !this._visionTaskRegistry) {
      [width, height] = [height, width];
    }
    if (width === 0 || height === 0 || !this.available) return false;
    image.width = width;
    image.height = height;
    let textureValid = false;
    if (!this.totalFrames) {
      if (this.program) this.useTexture();
      else return false;
      textureValid = this._textureValid;
      this._textureValid = true;
    } else {
      this.useTexture();
      textureValid = this._selfieTextureValid;
      this._selfieTextureValid = true;
    }
    if (this.width !== width || this.height !== height || !textureValid) {
      this.resize(width, height);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, image);
    }
    if (this._weixin) {
      gl.useProgram(this._prePrograme!);
      this.useTexture();
      if (this._segmentationMask) {
        const mask = this.getMaskTexture(this._segmentationMask);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, mask || null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this._lastMaskFbo || null);
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      this.useTexture();
      if (this._segmentationMask) {
        gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, width, height);
      } else {
        gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0, width, height, 0);
      }
    }
    try {
      if (this._selfieSegmentationHash && this._visionTaskRegistry) {
        const result = this._visionTaskRegistry.getResult(this._selfieSegmentationHash);
        if (this.totalFrames === 1 && this.context._canvas) this.resolvePreditReady();
        this.onPredict(result);
      }
    } catch (error) {
      if (this._onAbort) {
        this._onAbort(error);
      }
    }
    if (!this.totalFrames) {
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, this._bgTexture || null);
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, this._waterMarkTexture || null);
      // this.useBufferFrame();
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    if (this._visionTaskRegistry) this._visionTaskRegistry.resetHashResults();
    return false;
  }
  private centerFace() {
    if (!this._centerFace || !this._enableFaceCentering) return;
    const gl = this.context.ctx as WebGL2RenderingContext;
    this._centerFace.aspectRatio = gl.canvas.width / gl.canvas.height;
    this._centerFace.actionCentering(this.image);
    const { current, offset } = this._centerFace;
    if (!current) return;
    if (this.wasm.vbMode === VirtualBackgroundMode.Blur) {
      // 通过webgl矩阵操作模拟canvas2d的drawImage实现裁剪居中方案
      this.drawImage(current.sx, current.sy, current.cropWidth, current.cropHeight);
    }
    if (offset && this.wasm.vbMode === VirtualBackgroundMode.Image) {
      // 通过webgl矩阵操作缩放偏移主画面frame实现人像移动居中方案
      if (!this._mat4) return;
      const offsetMatrix = this._mat4.create();
      const { scaleRatio = 1, scaleOffsetX = 0, scaleOffsetY = 0 } = current;
      this._mat4.fromTranslation(offsetMatrix, [-offset.offsetX / gl.canvas.width + scaleOffsetX, scaleOffsetY, 0]);
      this._mat4.scale(offsetMatrix, offsetMatrix, [scaleRatio, scaleRatio, 1]);
      gl.uniformMatrix4fv(this._offsetMatrixLocation, false, offsetMatrix);
    }
  }
  private drawImage(
    srcX: number,
    srcY: number,
    srcWidth: number,
    srcHeight: number
  ) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    if (!this._mat4) return;
    const { width: texWidth, height: texHeight } = gl.canvas;
    const texMatrix = this._mat4.create();
    this._mat4.fromTranslation(texMatrix, [srcX / texWidth, 1.0 - (srcY + srcHeight) / texHeight, 0]);
    this._mat4.scale(texMatrix, texMatrix, [srcWidth / texWidth, srcHeight / texHeight, 1]);
    gl.uniformMatrix4fv(this._textureMatrixLocation, false, texMatrix);
  }
  close(): void {
    super.close();
    const gl = this.context.ctx as WebGL2RenderingContext;
    if (this._bgTexture) gl.deleteTexture(this._bgTexture);
    if (this._waterMarkTexture) gl.deleteTexture(this._waterMarkTexture);
    if (this._lastMaskTexture) gl.deleteTexture(this._lastMaskTexture);
    if (this._lastMaskFbo) gl.deleteFramebuffer(this._lastMaskFbo);
    if (this._prePrograme) gl.deleteProgram(this._prePrograme);
    if (this._postProcessing) this._postProcessing.close();
    this.wasm?.close();
  }
}
